/**
 * Sparks.jsx — friction sparks at the cart's wheels.
 * Active in Challenge mode when μ is high and the cart is moving:
 * visible feedback that high friction = grinding = wear.
 */
import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useGame } from "./store";

const COUNT = 40;

export default function Sparks() {
  const pointsRef = useRef();

  const { positions, velocities, life } = useMemo(() => {
    return {
      positions: new Float32Array(COUNT * 3),
      velocities: new Float32Array(COUNT * 3),
      life: new Float32Array(COUNT),
    };
  }, []);

  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05);
    const st = useGame.getState();
    const active = st.sparksActive && st.mode === "ON_TRACK" && !st.paused;
    const pts = pointsRef.current;
    if (!pts) return;
    pts.visible = active || life.some((l) => l > 0);
    if (!pts.visible) return;

    const [cx, cy, cz] = st.pos;
    const speed = st.v || 0;

    for (let i = 0; i < COUNT; i++) {
      life[i] -= dt * 2.2;
      if (life[i] <= 0 && active) {
        // Respawn at a wheel with a random kick (backward + sideways + up)
        life[i] = 0.4 + Math.random() * 0.5;
        positions[i * 3] = cx + (Math.random() - 0.5) * 1.6;
        positions[i * 3 + 1] = cy - 0.5;
        positions[i * 3 + 2] = cz + (Math.random() - 0.5) * 1.6;
        velocities[i * 3] = (Math.random() - 0.5) * 6;
        velocities[i * 3 + 1] = 2 + Math.random() * 4;
        velocities[i * 3 + 2] = (Math.random() - 0.5) * 6 - speed * 0.25;
      } else if (life[i] > 0) {
        velocities[i * 3 + 1] -= 12 * dt; // gravity
        positions[i * 3] += velocities[i * 3] * dt;
        positions[i * 3 + 1] += velocities[i * 3 + 1] * dt;
        positions[i * 3 + 2] += velocities[i * 3 + 2] * dt;
      } else {
        positions[i * 3 + 1] = -9999; // hide dead sparks
      }
    }
    pts.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={COUNT}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.35}
        color="#ffb347"
        transparent
        opacity={0.95}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}
